/**
 * HttpClient Custom Error
 */
export class HttpClientResponseError extends Error {
  /** @type {Response|HttpClientResponse} */
  response

  /**
   * @param {string} message
   * @param {object} options
   * @param {Response|HttpClientResponse} options.response
   * @param {Error} [options.cause]
   */
  constructor (message, { response, cause } = {}) {
    super(message, { cause })
    this.name = 'HttpClientResponseError'
    this.response = response
  }
}

/**
 * HttpClient が返す独自レスポンス。axios / Fetch の両方と異なる点があるので注意
 *
 * axios の AxiosResponse と比べて:
 *   - config / request プロパティはなし
 *   - data は先読みパース済みなので、axios 同様 await を重ねずにそのまま参照できる ( 互換 )
 *   - ただし data が持ち得る型は axios と異なる。詳細は data の説明を参照 ( 非互換 )
 *
 * Fetch の Response と比べて:
 *   - ok / redirected / url / type / bodyUsed / body および json(), text() 等のメソッドは持たない
 *   - 2xx 以外は ok を返す代わりに axios 同様 throw する。
 *     捕捉側は error.response.status / error.response.data で本オブジェクトを参照する
 *
 * body のパースについて:
 *   axios や Fetch の response.json() は Content-Type を問わず強制的に JSON パースできるが、
 *   HttpClient は Content-Type に application/json が含まれる場合のみ JSON パースする。
 *   そのため text/html や text/plain で JSON 本文が返る口ができた場合、data は文字列のままになる
 * @typedef {object} HttpClientResponse
 * @property {number} status HTTP ステータスコード
 * @property {string} statusText HTTP ステータスメッセージ
 * @property {Headers} headers Fetch の Headers。headers.get('content-type') のように取得する
 * @property {object|string|null} data パース済みボディ。
 *   Content-Type が application/json ならパース結果のオブジェクト、
 *   それ以外は文字列。204 および空ボディ ( axios では '' ) は null になる
 */

/**
 * HTTP client wrapper
 */
export class HttpClient {
  /** @type {*} */
  #client
  /** @type {object} */
  #defaultHeaders
  /** @type {string} */
  #origin

  /**
   * @param {object} [dependencies]
   * @param {string} [dependencies.origin]
   * @param {object} [dependencies.headers]
   * @param {*} [dependencies.client]
   */
  constructor ({
    origin = window.location.origin,
    headers = {},
    client = globalThis.fetch.bind(globalThis)
  } = {}) {
    this.#client = client
    this.#defaultHeaders = headers
    this.#origin = origin
  }

  /**
   * @param {string} url
   * @param {object} [config]
   * @returns {Promise<HttpClientResponse>}
   */
  async get (url, config = {}) {
    return this.request(
      url,
      'get',
      {
        ...config,
        headers: this.buildRequestHeaders({ url, method: 'get', headers: config.headers })
      }
    )
  }

  /**
   * @param {string} url
   * @param {*} [data]
   * @param {object} [config]
   * @returns {Promise<HttpClientResponse>}
   */
  async post (url, data, config = {}) {
    return this.request(
      url,
      'post',
      {
        ...config,
        data,
        headers: this.buildRequestHeaders({ url, method: 'post', headers: config.headers })
      }
    )
  }

  /**
   * @param {string} url
   * @param {*} [data]
   * @param {object} [config]
   * @returns {Promise<HttpClientResponse>}
   */
  async put (url, data, config = {}) {
    return this.request(
      url,
      'put',
      {
        ...config,
        data,
        headers: this.buildRequestHeaders({ url, method: 'put', headers: config.headers })
      }
    )
  }

  /**
   * @param {string} url
   * @param {any} [data]
   * @param {object} [config]
   * @returns {Promise<HttpClientResponse>}
   */
  async patch (url, data, config = {}) {
    return this.request(
      url,
      'patch',
      {
        ...config,
        data,
        headers: this.buildRequestHeaders({ url, method: 'patch', headers: config.headers })
      }
    )
  }

  /**
   * @param {string} url
   * @param {object} [config]
   * @returns {Promise<HttpClientResponse>}
   */
  async delete (url, config = {}) {
    return this.request(
      url,
      'delete',
      {
        ...config,
        headers: this.buildRequestHeaders({ url, method: 'delete', headers: config.headers })
      }
    )
  }

  /**
   * @param {string} url
   * @param {string} method
   * @param {object} [config]
   * @returns {Promise<HttpClientResponse>}
   * @throws {HttpClientResponseError}
   */
  async request (url, method, config = {}) {
    const normalizedMethod = method.toUpperCase()
    const { headers, body } = this.transformRequest(config)

    const response = await this.#client(url, { method: normalizedMethod, headers, body })

    return this.buildResponse(response)
  }

  /**
   * data 形式を適切に変換し、data に応じたヘッダーを設定する
   *   - data形式は FormData と JSON に対応
   *   - FormData はブラウザが適切な Content-Type を設定するため手動で設定しない
   * @param {*} config
   * @returns {{body: *, headers: object}}
   */
  transformRequest (config = {}) {
    const headers = { ...(config.headers || {}) }
    const { data } = config
    let body

    if (data !== undefined) {
      if (this.isFormData(data)) {
        body = data
      } else {
        body = JSON.stringify(data)
        if (!this.hasHeader(headers, 'Content-Type')) {
          headers['Content-Type'] = 'application/json'
        }
      }
    }

    return { headers, body }
  }

  /**
   * @param {*} data
   * @returns {boolean}
   */
  isFormData (data) {
    return typeof FormData !== 'undefined' && data instanceof FormData
  }

  /**
   * @param {object} headers
   * @param {string} name
   * @returns {boolean}
   */
  hasHeader (headers, name) {
    return Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase())
  }

  /**
   * Fetch の Response を独自の HttpClientResponse に変換する。
   * axios 互換は一部のみ ( 形状の詳細と非互換な点は HttpClientResponse の定義を参照 )。
   * 2xx以外はaxios同様にthrowし、error.response.status / error.response.data で参照できるようにする
   * @param {Response} response
   * @returns {Promise<HttpClientResponse>}
   * @throws {HttpClientResponseError} レスポンスが2xx以外の場合
   */
  async buildResponse (response) {
    const data = await this.parseResponseBody(response)
    const result = { status: response.status, statusText: response.statusText, headers: response.headers, data }

    if (!response.ok) {
      throw new HttpClientResponseError(`Request failed with status code ${response.status}`, { response: result })
    }

    return result
  }

  /**
   * Content-Type に従ってボディを自動パースする。
   * axios / Fetch の json() のような強制 JSON パースは行わないため、
   * text/html や text/plain で JSON が返る場合は文字列のままになる
   * @param {Response} response
   * @returns {Promise<object|string|null>}
   * @throws {HttpClientResponseError}
   */
  async parseResponseBody (response) {
    if (response.status === 204) {
      return null
    }

    const text = await response.text()
    if (text === '') {
      return null
    }

    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      try {
        return JSON.parse(text)
      } catch (cause) {
        throw new HttpClientResponseError(`Failed to parse response body as JSON: ${response.url}`, { response, cause })
      }
    }

    return text
  }

  /**
   * @param {object} [config]
   * @param {string} [config.url]
   * @param {string} [config.method]
   * @param {object} [config.headers]
   * @returns {object}
   */
  buildRequestHeaders ({ url, method, headers = {} } = {}) {
    const mergedHeaders = { ...this.#defaultHeaders, ...headers }

    if (!this.isNeedCsrfToken(url, method)) {
      delete mergedHeaders['X-CSRF-Token']
    }

    return mergedHeaders
  }

  /**
   * 以下の条件の場合にのみCSRFトークンが必要であると判断する
   * - メソッドがGET以外であること
   * - 同一Originであること
   * @param {string} url
   * @param {string} method
   * @returns {boolean}
   */
  isNeedCsrfToken (url, method) {
    if (method.toLowerCase() === 'get') {
      return false
    }

    return this.isSameOrigin(url)
  }

  /**
   * @param {string} url
   * @returns {boolean}
   */
  isSameOrigin (url) {
    return url.startsWith('/') || url.startsWith(this.#origin)
  }
}
